import mixpanel from "mixpanel-browser";

/*
 * The web paywall's VIEW event — the denominator that did not exist.
 *
 * QLearn has two analytics systems and a clean split between them. GTM
 * (src/utils/tracking.js) carries acquisition: landing_page_viewed,
 * paid_click_signup, user_registration, user_sign_in. Mixpanel carries
 * in-product behaviour: Create Quiz, Quiz completed, Subscription
 * requested/bought/cancelled/restored.
 *
 * Neither carried "the student was shown the offer". The full event inventory
 * on main confirms it, and `mixpanel.init` is called without `track_pageview`
 * so there is no implicit page event either. The one paywall-view event that
 * exists — Mobile Paywall Viewed, services/mobileFunnelAnalytics.js — is fired
 * behind `isNativeBillingRuntime`, i.e. iOS only.
 *
 * The consequence is that "conversion rate" has not been computable on web.
 * `Subscription bought` gives the numerator; without a view event the only
 * available denominator is registrations, which conflates two completely
 * different problems: students who never found the paywall (a routing and
 * discovery problem) and students who saw it and declined (a pricing and offer
 * problem). Those need opposite fixes, and the single ratio cannot tell them
 * apart.
 *
 * Mixpanel, not GTM, and not the mobile dual-fire client: the numerator
 * (`Subscription bought`) lives in Mixpanel, and a denominator in a different
 * system turns every conversion question into a cross-system join.
 *
 * THE NEIGHBOURS, because the inventory above omitted the family a reader most
 * needs and that omission is how funnels get built wrong. utils/tracking.js
 * already carries `paywall_entry_clicked` and `paywall_entry_shown` — the
 * IMMEDIATELY PRECEDING step in this same funnel (an upsell CTA was shown, an
 * upsell CTA was clicked). They are snake_case and dual-sinked to GTM and
 * Mixpanel; this one is Title Case and Mixpanel-only, matching the
 * `Subscription bought` family it has to divide into. Both conventions are
 * defensible and the split is deliberate, but anyone assembling
 * entry-shown -> entry-clicked -> paywall-viewed -> bought needs to know the
 * first two live elsewhere and under a different casing.
 */

export const PAYWALL_VIEWED_EVENT = "Paywall Viewed";

/*
 * `purchasable_plan_count` is deliberately the ONLY qualifier, and it is read
 * from the same expression the page hands to <SubscriptionsListView/> in the
 * same render — so it is what the student was actually shown, not a parallel
 * re-derivation that can drift from it.
 *
 * It replaces the obvious-looking `has_active_plan`. An existing subscriber
 * opening /subscriptions to check their billing history is not a conversion
 * opportunity and must not inflate the denominator — but subscription state
 * arrives from a SEPARATE request that may not have resolved when the paywall
 * first paints, so a boolean read at fire time would report `false` for
 * subscribers whose plan simply had not loaded yet. That is a silent,
 * plausible, one-directional error of exactly the kind this event exists to
 * avoid.
 *
 * The rendered count has no such race: the page already filters the active
 * plan out of the list it displays, so a subscriber with nothing left to buy
 * is `0`. Conversion rate is therefore
 *
 *     Subscription bought / Paywall Viewed WHERE purchasable_plan_count > 0
 *
 * and `purchasable_plan_count === 0` is not noise to discard — it is a real
 * dead end worth counting on its own: a student who reached the paywall and
 * was offered nothing.
 */
/*
 * `platform: "web"` mirrors mobileFunnelAnalytics' BASE_PROPERTIES
 * ({platform: 'ios', provider: 'REVENUECAT'}), and it is here to prevent one
 * specific analysis error: this event is WEB ONLY BY DESIGN, and its name does
 * not say so. Someone building a paywall funnel from `Paywall Viewed` would
 * silently exclude every iOS view — those live under `Mobile Paywall Viewed`.
 * With a platform on both, the two union into a single funnel that breaks down
 * by platform instead of quietly under-reporting one.
 */
const PLATFORM = "web";

export const trackPaywallViewed = ({
  route,
  purchasablePlanCount,
  plansLoadFailed = false,
} = {}) => {
  try {
    if (!route) return false;
    if (!Number.isFinite(purchasablePlanCount)) return false;

    mixpanel.track(PAYWALL_VIEWED_EVENT, {
      platform: PLATFORM,
      route,
      purchasable_plan_count: purchasablePlanCount,
      // A count of 0 otherwise conflates a subscriber with nothing left to buy
      // and a failed plans fetch. The first is a real state; the second is an
      // outage, and an outage must not read as "nobody had anything to buy".
      plans_load_failed: plansLoadFailed,
    });
    return true;
  } catch (e) {
    // Never throw: this sits on the paywall's render path, and an analytics
    // vendor failing must not take the purchase surface down with it.
    return false;
  }
};
